import { useAppAccessPermissions } from 'global/cnr/contexts/useHelpers/useAppAccessPermissions';
import { useAppUserAccess } from 'global/cnr/contexts/useHelpers/useAppUserAccess';
import _ from 'lodash';
import React, { Suspense, createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Ref, Sidebar } from 'semantic-ui-react';
import { FrameworkContext } from '../cnr/contexts/FrameworkContent';
import PageComponentProvider from '../cnr/contexts/PageComponentContext';
import PageDimmerProvider from '../cnr/contexts/PageDimmerContext';
import { ParentContext } from '../cnr/contexts/ParentContext';
import SplashContextProvider from '../cnr/contexts/SplashContext';
import { startTypes } from '../cnr/reducers/StartReducer';
import { uniqueKey } from '../common/keys';
import SuspenseDimmer from '../components/alerts/SuspenseDimmer';
import IconGrids from '../components/layout/IconGrids';
import Banner from '../components/sem/SemBanner';
import Banners from '../components/sem/SemBanners';
import AppSidebar from '../components/sidebars/AppSidebar';
import { allPageTypes } from "../enums/allPageTypes";
import { gEnums } from '../enums/globalEnums';
import { _animateTypes } from '../motions/AnimateComponent';
import MotionWait from '../motions/MotionWait';
import { DemoEvent } from '../viewSettings/events/DemoEvent';
import AppWrapper from '../wrappers/AppWrapper';
import DesktopWrapper from '../wrappers/DesktopWrapper';
import Wrapper from "../wrappers/Wrapper";
import AppContentAlt from './AppContentAlt';
import PageAreaContent from './PageAreaContent';
import PageNavigation from './PageNavigation';
import PageTopHeader from './PageTopHeader';
import { useRenders } from 'global/cnr/contexts/useHelpers/useRenders';
import { useStyles } from 'global/cnr/contexts/useHelpers/useStyles';
import PageDataProvider from 'global/cnr/contexts/PageDataContext';
import PageAreaProvider from 'global/cnr/contexts/PageAreaContext';
import PageAreaParentProvider from 'global/cnr/contexts/PageAreaParentContext';
import { useNavigate } from 'react-router-dom';

export const AppContentContext = createContext();

const QrCode = React.lazy(() => import("../pageItem/modification/pageItemActions/QrCode"));

/**
 * This is the Main App Content
 * @returns 
 */
const AppContent = () => {

  const { setStyleAndClass } = useStyles()

  const navigate = useNavigate();
  const _accessPrms = useAppAccessPermissions()

  const appUserAccess = useAppUserAccess()
  const { loggedIn: loggedIn_au, accessLevel } = appUserAccess ?? {}

  // parentContext
  const parentContext = useContext(ParentContext);
  const { states, fns, settings } = parentContext ?? {}
  const { currentSettings_state, appSettings_state, appUser_state, page_state, paps_state, transition_state } = states
  const { appUser_fns } = fns

  const { aps_global } = _.get(currentSettings_state, 'gvviSettings', {});
  const { aps_page } = _.get(page_state, 'pageSettings', {});

  const { clientSettings } = settings ?? {}
  const { global: global_client } = clientSettings ?? {}
  const { appSignIn: appSignIn_client } = global_client ?? {}
  const { redirectToCurrentEvent } = appSignIn_client ?? {}

  const { transitions } = transition_state ?? {}
  const transition_page = transitions ? transitions[_animateTypes.page] : null
  const { showTransition: showTransition_page } = transition_page ?? {}

  useRenders('AppContent', true, true, aps_page)

  // frameworkContext - not in parent
  const frameworkContext = useContext(FrameworkContext);
  const { framework_state } = frameworkContext ?? {}
  const {
    cnp,
    desktopMode,
    isMobileDevice,
    isStandAlone,
    permissionGroups,
    showAppBottomModal,
    showAppBottomSidebar,
    showAppQrCode,
    showSignIn,
    useShortLink,
  } = framework_state ?? {}

  // authContext 
  const { appUser, homePageResponded } = appUser_state ?? {}


  // appSettingsContext 
  const { themeSelected } = appSettings_state ?? {}
  let { showHomeScreenPrompt, showHomeScreenHelp } = appSettings_state ?? {}

  // papsContext 
  const { otherView, view, pathViews, rootPaths } = paps_state ?? {}
  const { events: eventKey } = pathViews ?? {}

  const { topMenu, navigation, appStatus, banners: app_banners, appNotifications, appInstallation, projectOptions, appSignIn } = aps_global ?? {}
  const { bannersLocation } = app_banners ?? {}
  const { forceInstall } = appSignIn ?? {}

  const _forceInstall = forceInstall && !isStandAlone && isMobileDevice

  const { useAppDarkMode, allowNewVersionUpdate } = projectOptions ?? {}
  const { menuLocation } = topMenu ?? {}
  const { showNavigation } = navigation ?? {}
  const { eventStatusType } = appStatus ?? {}

  const styleAndClass = setStyleAndClass({ item: gEnums.projectStyles.pageItemDimmer })

  // local state
  const [pageAccess, setPageAccess] = useState({ pending: true })
  const [allowAppView, setAllowAppView] = useState()
  const [showAppAccess, setShowAppAccess] = useState(false)


  const _pageAccess = useMemo(() => {

    if (!aps_page || !aps_global) return null;

    const { allow: _allowPageView } = _accessPrms.validateAccess_pageView();

    const _allowed = _allowPageView === gEnums.viewPermissionTypes.allow;

    return {
      pageAccess: { allowed: _allowed, allowPageView: _allowPageView, pending: false },
      allowAppView: appStatus ? appUser_fns.validateAccess_app(appUser, appStatus) : true
    };

  }, [aps_page, appUserAccess]);

  useEffect(() => {
    setPageAccess(_pageAccess?.pageAccess);
    setAllowAppView(_pageAccess?.allowAppView);
  }, [_pageAccess]);

  useEffect(() => {
    // redirect the appUser to the current event
    if (loggedIn_au && redirectToCurrentEvent && !pathViews.events && appUserAccess.activeEvents_appUser && accessLevel < gEnums.authLevels.appDataAdmin.value) {
      if (appUserAccess.activeEvents_appUser.length > 0) {
        const event_appUser = appUserAccess.activeEvents_appUser[0]
        const _cp = rootPaths.clients + 'events/' + event_appUser + '/landing'
        navigate(_cp)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectToCurrentEvent]);

  const contextRef = useRef()

  const appContent_state = {
    allowNewVersionUpdate,
    appInstallation,
    appNotifications,
    appSignIn,
    bannersLocation,
    css: { container: themeSelected ? 'theme-on theme-on-' + themeSelected : null, menu: menuLocation },
    desktopMode,
    eventKey,
    forceInstall: _forceInstall,
    homePageResponded,
    showHomeScreenHelp,
    showHomeScreenPrompt: showHomeScreenPrompt && !homePageResponded,
    useAppDarkMode,
  }

  const topNav = useMemo(() => {
    return <IconGrids key={uniqueKey('pcpic', 'icn', 'zz')} />
  }, [permissionGroups])

  const pageAreaContent = () => <PageAreaProvider>
    <PageDataProvider>
      <PageAreaParentProvider>
        <PageAreaContent />
      </PageAreaParentProvider>
    </PageDataProvider>
  </PageAreaProvider>

  const desktopPage = () =>
    <Ref innerRef={contextRef}>
      <DesktopWrapper
        topNav={topNav}
        banner={<Banner />}
        banners={<Banners />}
        menu={menuLocation === gEnums.menuLocations.top && <PageTopHeader />}
        content={pageAreaContent()}
        navigation={<PageNavigation />}
        // footer={desktopMode && < DesktopFooter />}
        innerRef={contextRef}
      ></DesktopWrapper>
    </Ref>

  const appPage = () => {
    return <AppWrapper
      banner={<Banner />}
      banners={<Banners />}
      content={pageAreaContent()}
      menu={menuLocation === gEnums.menuLocations.top && <PageTopHeader />}
      navigation={<PageNavigation />}
    ></AppWrapper>
  }

  const otherContent = () => {

    let oc;

    switch (otherView) {
      case allPageTypes.appProfile:
      case allPageTypes.appUserInfo:
      case allPageTypes.help:
      case allPageTypes.signIn:
        let cn;
        if (cnp) { cn += ' ' + cnp }
        oc = <Wrapper
          content={pageAreaContent()}
          css={{ container: cn, header: 'padd5' }}
        />
        break;
      default:
        oc = <PageAreaContent />
    }

    // if (desktopMode) {
    //   return <Ref innerRef={contextRef}>
    //     <Desktop
    //       headerContent={<PageTopHeader />}
    //       appContent={oc}
    //     />
    //   </Ref>
    // } else {
    return appPage()
    // }
  }

  const appMainContent = () => {
    if (otherView) {
      return otherContent()
    } else {
      return desktopMode ? desktopPage() : appPage()
    }
  }

  const mainContent = () => <AppSidebar>
    {appMainContent()}
  </AppSidebar>

  const appContentOther = () => <Suspense fallback={<SuspenseDimmer origin={'App Content'} />}>
    <QrCode qrCodeType={gEnums.qrCodeTypes.appLink} fromSidebar={true} useShortLink={useShortLink} />
  </Suspense>

  /**
   * 
   * @returns app content
   */
  const appContent = () => {
    if (showAppQrCode) {
      return appContentOther()
    } else if ((showSignIn && !loggedIn_au)) {
      // show the sign in page
      return <AppContentAlt view={view} pageAccess={pageAccess} appUser={appUser} loggedIn={loggedIn_au} fromDirect={true} />
    } else if (pageAccess?.allowed) {
      return mainContent()
    } else {
      return <AppContentAlt view={view} pageAccess={pageAccess} appUser={appUser} loggedIn={loggedIn_au} />
    }
  }

  /**
   * 
   * @returns Sidebar
   * this is inside a useMemo
   */
  const app_withSidebar = () => <Sidebar.Pushable
  // style={{ overflow: 'hidden' }}
  >
    {/* {showAppBottomModal && sidebar_appBottomModal(showAppBottomModal)} */}
    {/* {showAppBottomSidebar && sidebar_appBottom(showAppBottomSidebar)} */}
    <Sidebar.Pusher dimmed={showAppBottomSidebar || showAppBottomModal} className='h100 psh ac'
    >
      {appContent()}
    </Sidebar.Pusher>
  </Sidebar.Pushable>

  const pageContent = () => {

    switch (view) {
      case 'demo':
        return <DemoEvent />;
      default:
        // if (showAppAccess) {

        //   return <AppAccessPage />;
        // } else {
        switch (allowAppView) {
          // case gEnums.eventStatusTypes.restricted:
          //   return <RestrictedPage />;
          default:
            switch (eventStatusType) {
              // case gEnums.eventStatusTypes.underMaintenance:
              //   return <UnderMaintenancePage />;
              default:
                if (pageAccess?.pending) {
                  return (
                    <MotionWait inverted={true} styleAndClass={styleAndClass} startType={startTypes.pageItem} origin={'AppContent'} />
                  );
                } else {
                  return (
                    <AppContentContext.Provider value={{ appContent_state: { ...appContent_state } }}>
                      {app_withSidebar()}
                    </AppContentContext.Provider>
                  );
                }
            }
        }
      // }
    }
  }

  const appPageContent = () => {
    return showTransition_page ? (
      <PageDimmerProvider>{pageContent()}</PageDimmerProvider>
    ) : (
      pageContent()
    );
  }

  return <SplashContextProvider>
    <PageComponentProvider>
      {appPageContent()}
    </PageComponentProvider>
  </SplashContextProvider>

}

export default AppContent